import { getSizeInMB, getSizeInMBMap } from './getHeapSize'

class MemoryBoundedLRUCache {
  private readonly cache: Map<string, any>
  private readonly maxSize?: number // max size of the cache in MB
  private readonly maxItem: number // max number of items in the cache
  private recentActiveKey: string | null = null

  constructor(maxItem: number, maxSize?: number) {
    this.cache = new Map()
    this.maxItem = maxItem
    this.maxSize = maxSize
  }

  // Get an object from the cache
  get(key: string) {
    if (!this.cache.has(key)) return null
    const { value } = this.cache.get(key)
    if (key !== this.recentActiveKey) {
      this.cache.delete(key) // Remove the old position
      this.cache.set(key, { value, updatedAt: Date.now() }) // Re-insert at the end (most recent)
      this.recentActiveKey = key
    }
    return value
  }

  // Add or update an object
  put<T>(key: string, value: T) {
    if (this.cache.has(key)) {
      this.cache.delete(key) // Remove old entry to reinsert it as most recent
    } else if (this.cache.size >= this.maxItem) {
      // Remove the oldest item (first inserted item)
      const oldestKey = this.cache.keys().next().value
      if (oldestKey) {
        this.cache.delete(oldestKey)
      }
    }

    this.checkMemoryUsage(value)

    this.cache.set(key, { value, updatedAt: Date.now() }) // Add or update
  }

  // Remove an object from the cache
  delete(key: string) {
    this.cache.delete(key)
  }

  // Clear the cache
  clear() {
    this.cache.clear()
  }

  // Check if the value is too large to fit in the cache, if so, remove the oldest item until it fits
  // This is to prevent the cache from consuming too much memory
  // This is not needed if maxSize is not set
  private checkMemoryUsage<T>(value: T) {
    if (!this.maxSize) return

    let currentLRUCacheSize = getSizeInMBMap(this.cache)
    const valueSize = getSizeInMB(value)

    if (valueSize > this.maxSize) {
      this.clear()
      return
    }
    while (currentLRUCacheSize + valueSize > this.maxSize) {
      const oldestKey = this.cache.keys().next().value
      if (oldestKey) {
        this.cache.delete(oldestKey)
      }
      currentLRUCacheSize = getSizeInMBMap(this.cache)
    }
  }
}

export default MemoryBoundedLRUCache
